import { useEffect, useMemo, useRef, useState } from 'react';
import type { SelectableEntity } from '@modo-vivo/engine';
import {
  DemoAiBridge,
  RestaurantSimulator,
  type RestaurantSnapshot,
} from '@modo-vivo/scenario-restaurant';
import { InfoPanel } from './components/InfoPanel';
import { SimulatorPanel } from './components/SimulatorPanel';

export function App() {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<import('@modo-vivo/engine').RestaurantSceneApi | null>(null);
  const simRef = useRef<RestaurantSimulator | null>(null);
  const [snapshot, setSnapshot] = useState<RestaurantSnapshot | null>(null);
  const [selection, setSelection] = useState<SelectableEntity | null>(null);
  const [activeTable, setActiveTable] = useState(1);
  const [panelOpen, setPanelOpen] = useState(true);

  const aiBridge = useMemo(() => {
    const sim = simRef.current;
    if (!sim) {
      return null;
    }
    return new DemoAiBridge(
      {
        getTables: () => sim.snapshot().tables as unknown as Record<string, unknown>[],
        getRegisterStats: () => sim.snapshot().register as unknown as Record<string, unknown>,
        getKitchenQueue: () => sim.snapshot().kitchen as unknown as Record<string, unknown>,
        getStorage: () => ({ items: sim.snapshot().storage }) as Record<string, unknown>,
        getEmployees: () => sim.snapshot().employees as unknown as Record<string, unknown>[],
        getCharacterPositions: () =>
          sim.snapshot().characters.map((c) => ({ id: c.id, position: c.position })),
      },
      (cmd) => {
        if (cmd && gameRef.current) {
          gameRef.current.applyHighlights({
            targetIds: cmd.targetIds,
            style: cmd.style === 'badge' ? 'outline' : cmd.style,
            durationMs: cmd.durationMs,
          });
        }
      },
    );
  }, [snapshot]);

  useEffect(() => {
    const sim = new RestaurantSimulator();
    simRef.current = sim;
    const unsub = sim.subscribe(setSnapshot);

    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      sim.tick(now - last);
      last = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      unsub();
      simRef.current = null;
    };
  }, []);

  useEffect(() => {
    let destroyed = false;
    let game: import('@modo-vivo/engine').RestaurantSceneApi | null = null;

    const boot = async () => {
      if (!mountRef.current || gameRef.current) {
        return;
      }
      try {
        const { createRestaurantGame } = await import('@modo-vivo/engine');
        if (destroyed || !mountRef.current) {
          return;
        }
        game = createRestaurantGame(mountRef.current);
        gameRef.current = game;
        game.onSelect(setSelection);
      } catch (error) {
        console.error('No se pudo iniciar el motor gráfico', error);
      }
    };

    void boot();

    return () => {
      destroyed = true;
      game?.destroy();
      gameRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!snapshot || !gameRef.current) {
      return;
    }
    gameRef.current.setCharacters(snapshot.characters);
    gameRef.current.setTableStates(
      snapshot.tables.map((t) => ({
        number: t.number,
        status: t.status,
        occupied: t.customers.length > 0 || t.status === 'ocupada',
        highlight: selection?.id === `table-${t.number}`,
      })),
    );
  }, [snapshot, selection]);

  const sim = simRef.current;

  return (
    <div className={`app-shell ${panelOpen ? '' : 'panel-collapsed'}`}>
      <header className="app-header">
        <div className="brand">
          <h1>Modo Vivo</h1>
          <p>Restaurante Tycoon 3D estilizado — demo simulada</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="panel-toggle"
            onClick={() => setPanelOpen((v) => !v)}
            aria-expanded={panelOpen}
          >
            {panelOpen ? 'Ocultar panel' : 'Mostrar panel'}
          </button>
          <span className="sim-badge" title="Todos los datos son ficticios">
            ● Simulación
          </span>
        </div>
      </header>

      <main className="canvas-wrap">
        <div ref={mountRef} className="canvas-mount" aria-label="Escenario del restaurante" />
        <p className="hint" style={{ position: 'absolute', left: 12, bottom: 8, margin: 0 }}>
          Arrastrá para mover la cámara · Rueda del mouse para zoom · Clic o toque en mesas y zonas
        </p>
      </main>

      <aside className="side-panel" hidden={!panelOpen}>
        <SimulatorPanel
          sim={sim}
          activeTable={activeTable}
          onActiveTableChange={setActiveTable}
          snapshot={snapshot}
        />
        <InfoPanel selection={selection} snapshot={snapshot} aiBridge={aiBridge} />
      </aside>
    </div>
  );
}
